import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link, usePage } from '@inertiajs/react';

import Breadcrumbs from '@/components/public/ui/Breadcrumbs';
import Container from '@/components/public/ui/Container';
import Icon from '@/components/public/ui/Icon';
import Seo from '@/components/public/Seo';
import { CategoryBadgeColor } from '@/data/pages/news';
import { trackUmamiEvent } from '@/lib/analytics';
import { proseClasses } from '@/lib/markdown';

type NewsPost = {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  category: string | null;
  date: string | null;
  body: string;
  imageUrl: string | null;
};

type Props = {
  post: NewsPost;
};

// Category accent for the editorial eyebrow (text + the short rule before it).
const CATEGORY_ACCENT: Record<
  'primary' | 'secondary' | 'tertiary',
  { text: string; tick: string }
> = {
  primary: { text: 'text-primary', tick: 'bg-primary' },
  secondary: { text: 'text-secondary', tick: 'bg-secondary' },
  tertiary: { text: 'text-tertiary', tick: 'bg-tertiary' },
};

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('pt-PT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function NewsPiece() {
  const { post } = usePage<Props>().props;

  const accent =
    CATEGORY_ACCENT[(post.category ? CategoryBadgeColor[post.category] : null) ?? 'primary'];
  const readingTime = Math.max(1, Math.ceil(post.body.split(/\s+/).length / 200));

  const pageUrl =
    typeof window !== 'undefined'
      ? encodeURIComponent(`${window.location.origin}/news/${post.slug}`)
      : '';
  const pageTitle = encodeURIComponent(post.title);

  return (
    <>
      <Seo title={post.title} description={post.excerpt ?? undefined} />

      <Container as="article" className="max-w-2xl pt-12 pb-24 md:pt-16">
        <Breadcrumbs items={[{ label: 'Notícias', href: '/news' }, { label: post.title }]} />

        {/* Masthead */}
        <header>
          {post.category && (
            <span
              className={`inline-flex items-center gap-2.5 text-[11px] font-bold tracking-[0.14em] uppercase ${accent.text}`}
            >
              <span className={`inline-block h-0.5 w-5 rounded-full ${accent.tick}`} />
              {post.category}
            </span>
          )}
          <h1 className="font-headline text-on-surface mt-3.5 text-3xl leading-[1.12] font-semibold md:text-[2.5rem]">
            {post.title}
          </h1>
          <div className="text-on-surface-light mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {post.date && (
              <>
                <span>{formatDate(post.date)}</span>
                <span className="bg-line-strong h-1 w-1 rounded-full" />
              </>
            )}
            <span>{readingTime} min de leitura</span>
          </div>
          {post.excerpt && (
            <p className="font-headline text-on-surface-light mt-5 text-lg leading-relaxed font-normal italic md:text-xl">
              {post.excerpt}
            </p>
          )}
        </header>

        {/* Cover */}
        {post.imageUrl && (
          <figure className="mt-8">
            <img
              src={post.imageUrl}
              alt={post.title}
              className="border-line aspect-[16/9] w-full rounded-xl border object-cover"
            />
          </figure>
        )}

        {/* Body */}
        <div className={`mt-8 ${proseClasses}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body}</ReactMarkdown>
        </div>

        {/* Footer */}
        <footer className="border-line mt-10 flex flex-wrap items-center justify-between gap-4 border-t pt-6">
          <Link
            href="/news"
            className="text-primary hover:text-primary-dark inline-flex items-center gap-2 text-sm font-semibold transition-colors"
          >
            <Icon name="arrow_back" size="sm" />
            Voltar às Notícias
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-on-surface-light text-xs">Partilhar</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackUmamiEvent('news_share', { slug: post.slug, method: 'facebook' })}
              className="border-line bg-primary/[0.06] text-primary hover:bg-primary/12 flex h-8 w-8 items-center justify-center rounded-full border transition-colors"
              aria-label="Partilhar no Facebook"
            >
              <Icon name="share" size="sm" />
            </a>
            <a
              href={`mailto:?subject=${pageTitle}&body=${pageUrl}`}
              onClick={() => trackUmamiEvent('news_share', { slug: post.slug, method: 'email' })}
              className="border-line bg-primary/[0.06] text-primary hover:bg-primary/12 flex h-8 w-8 items-center justify-center rounded-full border transition-colors"
              aria-label="Partilhar por email"
            >
              <Icon name="mail" size="sm" />
            </a>
          </div>
        </footer>
      </Container>
    </>
  );
}
